/**
 * SEO service page configs for Tools & Bubbles.
 * Each entry powers a dedicated service landing page (e.g. /deep-cleaning-kochi).
 */

import { KEYWORD_SERVICE_PAGE_CONFIGS } from './keywordServicePages.js';

export interface ServicePageConfig {
  slug: string;
  /** Display name for the service, e.g. "Deep Cleaning Service" */
  serviceName: string;
  /** Short for meta/headings, e.g. "Deep Cleaning Kochi" */
  serviceTitle: string;
  hero: {
    eyebrow: string;
    title: string;
    description: string;
    highlights: { icon: string; text: string }[];
  };
  servicesIncluded: string[];
  areasWeServe: string[];
  whyChoose: { icon: string; title: string; description: string }[];
  reviews: { name: string; location: string; rating: number; text: string; date: string }[];
  cta: {
    title: string;
    description: string;
    phoneText?: string;
  };
  seo: {
    metaTitle: string;
    metaDescription: string;
    keywords: string;
    introParagraphs: string[];
    /** Optional long-form SEO sections (H2 + paragraphs + bullets) */
    sections?: { heading: string; paragraphs: string[]; bulletList?: string[] }[];
    conclusion?: string;
  };
  /** Optional FAQ for schema */
  faq?: { question: string; answer: string }[];
  /** When set, Book Now opens the wizard with this `service_pricing.slug` pre-selected */
  bookingTierSlug?: string;
  /** Rich guide sections (3M/Kärcher, safety, AMC, etc.) + standard footer on template */
  usePremiumTierGuide?: boolean;
  /** Optional images: hero, before/after (for conversion), and gallery */
  images?: {
    hero?: string;
    beforeAfter?: ReadonlyArray<{ before: string; after: string; caption?: string }>;
    gallery?: ReadonlyArray<string>;
  };
  /** Tier listing from-price (residential/commercial size pages); hero/CTA compose via formatMoney */
  listingFromAmount?: number;
}

const defaultWhyChoose = [
  {
    icon: 'Shield',
    title: 'Trained professional cleaners',
    description: 'Background-verified staff with supervisor check-ins and quality assurance.',
  },
  {
    icon: 'Sparkles',
    title: 'Eco-friendly cleaning products',
    description: 'Safe, effective products for your family and pets.',
  },
  {
    icon: 'CheckCircle',
    title: 'Transparent pricing',
    description: 'No hidden charges. See pricing upfront when you book online.',
  },
  {
    icon: 'Star',
    title: '100% satisfaction guarantee',
    description: 'We re-clean at no extra cost if you are not happy with the result.',
  },
];

const defaultReviews = [
  {
    name: 'Anjali T.',
    location: 'Kakkanad',
    rating: 5,
    text: 'Best deep cleaning service in Kochi. The team cleaned our entire apartment perfectly.',
    date: '2025',
  },
  {
    name: 'Vivek K.',
    location: 'Edappally',
    rating: 5,
    text: 'Professional and punctual. Our home has never looked better.',
    date: '2025',
  },
  {
    name: 'Priya M.',
    location: 'Kaloor',
    rating: 5,
    text: 'Highly recommend Tools & Bubbles. Transparent pricing and great results.',
    date: '2025',
  },
];

/** Service-specific before/after and gallery – same room, same fittings, only cleaning. */
const SERVICE_IMAGES = {
  deepCleaning: {
    hero: '/images/hero-deep-cleaning.png',
    beforeAfter: [
      {
        before: '/images/indian-home-kitchen-before.png',
        after: '/images/indian-home-kitchen-after.png',
        caption: 'Kitchen deep clean – Indian home in Kakkanad, Kochi',
      },
      {
        before: '/images/indian-home-living-before.png',
        after: '/images/indian-home-living-after.png',
        caption: 'Living area – before and after, Tools & Bubbles Kochi',
      },
      {
        before: '/images/indian-home-bathroom-before.png',
        after: '/images/indian-home-bathroom-after.png',
        caption: 'Bathroom deep clean – Kochi',
      },
    ],
    gallery: [
      '/images/tools-bubbles-team-deep-clean.png',
      '/images/tools-bubbles-collage-4areas.png',
      '/images/tools-bubbles-living-room-cleaning.png',
      '/images/3m_cleaning_in_kochi.png',
      '/images/karcher_cleaning_in_kochi.png',
      '/images/cleaning_service_in_kochi.png',
    ],
  },
  home: {
    hero: '/images/hero-home.png',
    beforeAfter: [
      {
        before: '/images/indian-home-kitchen-before.png',
        after: '/images/indian-home-kitchen-after.png',
        caption: 'Kitchen – before and after cleaning, Kochi',
      },
      {
        before: '/images/indian-home-living-before.png',
        after: '/images/indian-home-living-after.png',
        caption: 'Living room – Tools & Bubbles',
      },
    ],
    gallery: [
      '/images/tools-bubbles-living-room-cleaning.png',
      '/images/tools-bubbles-team-deep-clean.png',
      '/images/3m_cleaning_in_kochi.png',
      '/images/karcher_cleaning_in_kochi.png',
      '/images/cleaning_service_in_kochi.png',
    ],
  },
  villa: {
    hero: '/images/hero-villa.png',
    beforeAfter: [
      {
        before: '/images/indian-home-kitchen-before.png',
        after: '/images/indian-home-kitchen-after.png',
        caption: 'Kitchen – before and after cleaning, Kochi',
      },
      {
        before: '/images/indian-home-living-before.png',
        after: '/images/indian-home-living-after.png',
        caption: 'Living room – Tools & Bubbles',
      },
    ],
    gallery: [
      '/images/tools-bubbles-living-room-cleaning.png',
      '/images/tools-bubbles-team-deep-clean.png',
      '/images/3m_cleaning_in_kochi.png',
      '/images/karcher_cleaning_in_kochi.png',
      '/images/cleaning_service_in_kochi.png',
    ],
  },
  apartment: {
    hero: '/images/hero-apartment.png',
    beforeAfter: [
      {
        before: '/images/indian-home-kitchen-before.png',
        after: '/images/indian-home-kitchen-after.png',
        caption: 'Kitchen – before and after cleaning, Kochi',
      },
      {
        before: '/images/indian-home-living-before.png',
        after: '/images/indian-home-living-after.png',
        caption: 'Living room – Tools & Bubbles',
      },
    ],
    gallery: [
      '/images/tools-bubbles-living-room-cleaning.png',
      '/images/tools-bubbles-team-deep-clean.png',
      '/images/3m_cleaning_in_kochi.png',
      '/images/karcher_cleaning_in_kochi.png',
      '/images/cleaning_service_in_kochi.png',
    ],
  },
  kitchen: {
    hero: '/images/hero-kitchen.png',
    beforeAfter: [
      {
        before: '/images/indian-home-kitchen-before.png',
        after: '/images/indian-home-kitchen-after.png',
        caption: 'Kitchen deep clean – Indian home, Kochi',
      },
    ],
    gallery: [
      '/images/tools-bubbles-kitchen-cleaning.png',
      '/images/tools-bubbles-collage-4areas.png',
      '/images/3m_cleaning_in_kochi.png',
      '/images/karcher_cleaning_in_kochi.png',
    ],
  },
  bathroom: {
    hero: '/images/hero-bathroom.png',
    beforeAfter: [
      {
        before: '/images/indian-home-bathroom-before.png',
        after: '/images/indian-home-bathroom-after.png',
        caption: 'Bathroom deep clean – Kochi',
      },
    ],
    gallery: [
      '/images/tools-bubbles-collage-4areas.png',
      '/images/3m_cleaning_in_kochi.png',
      '/images/karcher_cleaning_in_kochi.png',
    ],
  },
  bedroom: {
    hero: '/images/hero-bedroom.png',
    beforeAfter: [
      {
        before: '/images/indian-home-bedroom-before.png',
        after: '/images/indian-home-bedroom-after.png',
        caption: 'Bedroom & mattress cleaning – Kochi',
      },
    ],
    gallery: [
      '/images/tools-bubbles-bedroom-cleaning.png',
      '/images/tools-bubbles-collage-4areas.png',
      '/images/cleaning_service_in_kochi.png',
      '/images/3m_cleaning_in_kochi.png',
    ],
  },
  sofa: {
    hero: '/images/hero-sofa.png',
    beforeAfter: [
      {
        before: '/images/sofa-cleaning-before.png',
        after: '/images/sofa-cleaning-after.png',
        caption: 'Sofa & upholstery cleaning – Kochi',
      },
    ],
    gallery: [
      '/images/tools-bubbles-living-room-cleaning.png',
      '/images/tools-bubbles-collage-services.png',
      '/images/sofa-cleaning-after.png',
      '/images/cleaning_service_in_kochi.png',
    ],
  },
  carpet: {
    hero: '/images/hero-carpet.png',
    beforeAfter: [
      {
        before: '/images/carpet-cleaning-before.png',
        after: '/images/carpet-cleaning-after.png',
        caption: 'Carpet cleaning – Kochi',
      },
    ],
    gallery: [
      '/images/tools-bubbles-collage-services.png',
      '/images/carpet-cleaning-after.png',
      '/images/karcher_cleaning_in_kochi.png',
    ],
  },
  office: {
    hero: '/images/hero-office.png',
    beforeAfter: [
      {
        before: '/images/office-cleaning-before.png',
        after: '/images/office-cleaning-after.png',
        caption: 'Office cleaning – Kochi',
      },
    ],
    gallery: ['/images/office-cleaning-after.png', '/images/cleaning_service_in_kochi.png'],
  },
  postConstruction: {
    hero: '/images/hero-post-construction-trolley.png',
    beforeAfter: [
      {
        before: '/images/post-construction-before.png',
        after: '/images/post-construction-after.png',
        caption: 'Post-construction cleaning – Kochi',
      },
    ],
    gallery: [
      '/images/hero-post-construction-trolley.png',
      '/images/post-construction-after.png',
      '/images/karcher_cleaning_in_kochi.png',
    ],
  },
  restaurant: {
    hero: '/images/hero-restaurant.png',
    beforeAfter: [
      {
        before: '/images/restaurant-cleaning-before.png',
        after: '/images/restaurant-cleaning-after.png',
        caption: 'Restaurant cleaning – Kochi',
      },
    ],
    gallery: ['/images/restaurant-cleaning-after.png', '/images/3m_cleaning_in_kochi.png'],
  },
  showroom: {
    hero: '/images/hero-showroom.png',
    beforeAfter: [
      {
        before: '/images/showroom-cleaning-before.png',
        after: '/images/showroom-cleaning-after.png',
        caption: 'Showroom cleaning – Kochi',
      },
    ],
    gallery: ['/images/showroom-cleaning-after.png', '/images/cleaning_service_in_kochi.png'],
  },
  clinic: {
    hero: '/images/hero-clinic.png',
    beforeAfter: [
      {
        before: '/images/clinic-cleaning-before.png',
        after: '/images/clinic-cleaning-after.png',
        caption: 'Clinic cleaning – Kochi',
      },
    ],
    gallery: ['/images/clinic-cleaning-after.png', '/images/3m_cleaning_in_kochi.png'],
  },
  warehouse: {
    hero: '/images/hero-warehouse.png',
    beforeAfter: [
      {
        before: '/images/warehouse-cleaning-before.png',
        after: '/images/warehouse-cleaning-after.png',
        caption: 'Warehouse cleaning – Kochi',
      },
    ],
    gallery: ['/images/warehouse-cleaning-after.png', '/images/karcher_cleaning_in_kochi.png'],
  },
  infopark: {
    hero: '/images/hero-infopark-kakkanad.png',
    beforeAfter: [
      {
        before: '/images/office-cleaning-before.png',
        after: '/images/office-cleaning-after.png',
        caption: 'Office deep clean – Infopark, Kakkanad',
      },
    ],
    gallery: [
      '/images/tools-bubbles-team-deep-clean.png',
      '/images/3m_cleaning_in_kochi.png',
      '/images/karcher_cleaning_in_kochi.png',
      '/images/cleaning_service_in_kochi.png',
      '/images/tools-bubbles-collage-services.png',
    ],
  },
  moveIn: {
    hero: '/images/hero-move-in.png',
    beforeAfter: [
      {
        before: '/images/indian-home-kitchen-before.png',
        after: '/images/indian-home-kitchen-after.png',
        caption: 'Kitchen – handover clean, Kochi',
      },
      {
        before: '/images/indian-home-bathroom-before.png',
        after: '/images/indian-home-bathroom-after.png',
        caption: 'Bathroom – move-out ready, Kochi',
      },
    ],
    gallery: [
      '/images/tools-bubbles-team-deep-clean.png',
      '/images/tools-bubbles-collage-4areas.png',
      '/images/3m_cleaning_in_kochi.png',
      '/images/karcher_cleaning_in_kochi.png',
    ],
  },
} as const;

/** Shared before/after + gallery for pricing-tier landing pages (custom AI hero per tier). */
function tierLandingImageSet(heroPath: string): NonNullable<ServicePageConfig['images']> {
  return {
    hero: heroPath,
    beforeAfter: [...SERVICE_IMAGES.home.beforeAfter],
    gallery: [...SERVICE_IMAGES.home.gallery],
  };
}

export const SERVICE_PAGE_CONFIGS: Record<string, ServicePageConfig> = {
  'deep-cleaning-kochi': {
    slug: 'deep-cleaning-kochi',
    serviceName: 'Deep Cleaning Service',
    serviceTitle: 'Deep Cleaning Kochi',
    hero: {
      eyebrow: 'Professional deep cleaning across Kochi',
      title: 'Deep Cleaning Service in Kochi',
      description: `Tools & Bubbles provides professional deep cleaning services in Kochi, Ernakulam, Kakkanad, Edappally, Vyttila, and nearby areas. Our trained team removes deep dirt, grease, and bacteria from every corner of your home.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Trained professionals' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Kitchen degreasing',
      'Bathroom descaling',
      'Floor scrubbing',
      'Fan & light cleaning',
      'Sofa vacuuming',
      'Window cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book your deep cleaning service in Kochi today',
      description: 'Same-day slots available. Call or book online.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Deep Cleaning Service Kochi | Home Deep Cleaning | Tools & Bubbles',
      metaDescription: `Professional deep cleaning service in Kochi. Kitchen degreasing, bathroom descaling, floor scrubbing. Serving Kakkanad, Edappally, Vyttila and all Kochi. Book online or call 9995216660.`,
      keywords:
        'deep cleaning kochi, home deep cleaning kochi, apartment deep cleaning kochi, deep cleaning service kochi, house deep cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides professional deep cleaning services in Kochi, Ernakulam, Kakkanad, Edappally, Vyttila, and nearby areas. Our trained team removes deep dirt, grease, and bacteria from every corner of your home. Whether you live in an apartment in Kakkanad, a villa in Tripunithura, or a house in Kaloor, we bring the same high standards of deep cleaning to every booking.`,
        `Deep cleaning goes beyond regular dusting and mopping. It includes kitchen degreasing, bathroom descaling, floor scrubbing, fan and light cleaning, sofa vacuuming, and window cleaning. We use eco-friendly products safe for children and pets, and offer transparent pricing with same-day and next-day slots across Kochi.`,
        `Thousands of families in Kochi trust Tools & Bubbles for deep cleaning before festivals, after parties, or as a seasonal refresh. Our crew is background-verified, supervised on-site, and trained to use professional equipment so your home looks and feels spotless.`,
      ],
      sections: [
        {
          heading: 'What is included in our deep cleaning service?',
          paragraphs: [
            `Our deep cleaning service in Kochi covers every room and high-touch area. In the kitchen we degrease countertops, backsplashes, and appliance exteriors; clean exhaust and chimney filters; and sanitize sinks. In bathrooms we descale taps and tiles, clean grout, sanitise toilets and basins, and wipe mirrors and cabinets.`,
            `We also scrub and mop all floors, dust and wipe ceiling fans and light fixtures, vacuum sofas and upholstery, and clean windows and balcony areas. You can add services like carpet cleaning, mattress cleaning, or post-construction cleanup for a complete refresh.`,
          ],
          bulletList: [
            'Kitchen degreasing and chimney cleaning',
            'Bathroom descaling and sanitisation',
            'Floor scrubbing and mopping in all rooms',
            'Fan, light, and switch cleaning',
            'Sofa vacuuming and upholstery care',
            'Window and balcony cleaning',
          ],
        },
        {
          heading: 'Areas we serve for deep cleaning in Kochi',
          paragraphs: [
            `We provide deep cleaning services across Kochi and Ernakulam district. Our teams regularly serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and neighbouring localities. Same-day or next-day slots are often available when you book online or call 9995216660.`,
          ],
          bulletList: [
            'Kakkanad, Infopark, SmartCity',
            'Edappally, Kaloor, Palarivattom',
            'Vyttila, Kadavanthra, Marine Drive',
            'Tripunithura, Aluva, Kalamassery',
            'Angamaly and all Kochi suburbs',
          ],
        },
        {
          heading: 'Why choose Tools & Bubbles for deep cleaning?',
          paragraphs: [
            `We are one of Kochi’s most trusted names for deep cleaning. Our staff are trained, background-checked, and supervised so you get consistent quality. We use eco-friendly products, offer upfront pricing with no hidden charges, and back every job with a satisfaction guarantee. Book online in under a minute or call us for a quick quote.`,
          ],
        },
      ],
      conclusion:
        'Book your deep cleaning service in Kochi today. Call 9995216660 or book online for instant confirmation. Same-day slots available in Kakkanad, Edappally, Vyttila, and across Kochi.',
    },
    faq: [
      {
        question: 'What is included in deep cleaning?',
        answer:
          'Deep cleaning includes kitchen degreasing, bathroom descaling, floor scrubbing, fan and light cleaning, sofa vacuuming, and window cleaning. We use eco-friendly products and cover all rooms.',
      },
      {
        question: 'Do you offer same-day deep cleaning in Kochi?',
        answer:
          'Yes. We often have same-day slots in Kakkanad, Edappally, Vyttila, and other areas. Book online or call 9995216660 to check availability.',
      },
      {
        question: 'How much does deep cleaning cost in Kochi?',
        answer:
          'Pricing depends on property size (1BHK, 2BHK, 3BHK, villa). You can see live pricing when you book online. We offer transparent rates with no hidden charges.',
      },
      {
        question: 'Which areas do you cover for deep cleaning?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and all of Kochi and Ernakulam.',
      },
    ],
    images: SERVICE_IMAGES.deepCleaning,
  },

  'home-cleaning-kochi': {
    slug: 'home-cleaning-kochi',
    serviceName: 'Home Cleaning Service',
    serviceTitle: 'Home Cleaning Kochi',
    hero: {
      eyebrow: 'Regular & deep home cleaning',
      title: 'Home Cleaning Service in Kochi',
      description: `Professional home cleaning in Kochi—apartments, villas, and independent houses. Regular and deep cleaning in Kakkanad, Edappally, Kaloor, Vyttila, and across Ernakulam.`,
      highlights: [
        { icon: 'Clock', text: 'Flexible scheduling' },
        { icon: 'Shield', text: 'Trusted cleaners' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Living & bedroom cleaning',
      'Kitchen & bathroom sanitization',
      'Dusting & mopping',
      'Balcony & common area cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book home cleaning in Kochi today',
      description: 'One-time or recurring. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Home Cleaning Service Kochi | House Cleaning | Tools & Bubbles',
      metaDescription: `Professional home cleaning service in Kochi. Apartments, villas. Kakkanad, Edappally, Vyttila. Same-day booking. Call 9995216660.`,
      keywords:
        'home cleaning kochi, house cleaning kochi, cleaning service kochi, home cleaning service kochi, professional cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles offers professional home cleaning services in Kochi for apartments, villas, and independent houses. We serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly and nearby areas.`,
        `You can book one-time home cleaning or set up a recurring schedule. Our team uses eco-friendly products and follows a clear checklist so every room is covered—living areas, bedrooms, kitchen, bathrooms, and balconies.`,
        `Whether you need house cleaning in Kochi after a busy week or regular upkeep for your flat in Kakkanad or Edappally, we offer transparent pricing and flexible slots. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What does home cleaning include?',
          paragraphs: [
            `Our home cleaning service covers living and bedroom cleaning, kitchen and bathroom sanitisation, dusting and mopping, and balcony and common area cleaning. We bring our own equipment and safe, eco-friendly products. You can add deep cleaning, sofa cleaning, or carpet cleaning for a more thorough job.`,
          ],
          bulletList: [
            'Living room and bedroom dusting and vacuuming',
            'Kitchen and bathroom sanitisation',
            'Floor mopping and balcony cleaning',
            'Organised and clutter-free finish',
          ],
        },
        {
          heading: 'Where do you provide home cleaning in Kochi?',
          paragraphs: [
            `We provide home cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby localities. Same-day and next-day slots are often available.`,
          ],
        },
      ],
      conclusion:
        'Book your home cleaning in Kochi today. Call 9995216660 or book online for instant confirmation.',
    },
    faq: [
      {
        question: 'What is included in home cleaning?',
        answer:
          'Home cleaning includes living and bedroom cleaning, kitchen and bathroom sanitisation, dusting, mopping, and balcony cleaning. We use eco-friendly products.',
      },
      {
        question: 'Can I book recurring home cleaning in Kochi?',
        answer:
          'Yes. You can book one-time or set up weekly or fortnightly home cleaning. Book online or call 9995216660 to discuss a schedule.',
      },
      {
        question: 'Do you serve my area in Kochi?',
        answer:
          'We serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi suburbs.',
      },
      {
        question: 'Do you provide maid service in Kochi?',
        answer:
          'No. Tools & Bubbles does not provide maid service or maid placement. We send a professional cleaning crew for booked home-cleaning jobs, with chemicals, machines and a checklist.',
      },
    ],
    images: SERVICE_IMAGES.home,
  },

  'apartment-cleaning-kochi': {
    slug: 'apartment-cleaning-kochi',
    serviceName: 'Apartment Cleaning Service',
    serviceTitle: 'Apartment Cleaning Kochi',
    hero: {
      eyebrow: 'Apartments & high-rises',
      title: 'Apartment Cleaning Service in Kochi',
      description: `Professional apartment cleaning in Kochi for 1BHK, 2BHK, 3BHK and larger. Deep cleaning and regular cleaning across Kakkanad, Edappally, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Verified staff' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Full apartment deep cleaning',
      'Kitchen & bathroom focus',
      'Floor & balcony cleaning',
      'Dusting & mopping',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book apartment cleaning in Kochi',
      description: 'Transparent pricing by size. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Apartment Cleaning Kochi | Flat Cleaning Service | Tools & Bubbles',
      metaDescription: `Apartment cleaning service in Kochi. 1BHK, 2BHK, 3BHK. Kakkanad, Edappally, Vyttila. Deep & regular cleaning. Call 9995216660.`,
      keywords: 'apartment cleaning kochi, flat cleaning kochi, apartment deep cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides professional apartment cleaning in Kochi for 1BHK, 2BHK, 3BHK and larger flats. We serve all major areas including Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery and Angamaly.`,
        `Whether you need a one-time deep clean for your flat in Kakkanad or regular apartment cleaning in Edappally or Vyttila, we price by size so you see the cost upfront. Our teams are used to working in high-rises and gated communities and will coordinate with building security when needed.`,
      ],
      sections: [
        {
          heading: 'What is included in apartment cleaning?',
          paragraphs: [
            `Apartment cleaning includes full interior deep cleaning, kitchen and bathroom focus, floor and balcony cleaning, and dusting and mopping. We tailor the checklist to your flat size (1BHK, 2BHK, 3BHK) and can add sofa cleaning, carpet cleaning, or post-construction cleanup.`,
          ],
          bulletList: [
            'Full apartment deep cleaning',
            'Kitchen and bathroom sanitisation',
            'Floor and balcony cleaning',
            'Dusting and mopping in all rooms',
          ],
        },
        {
          heading: 'Apartment cleaning areas in Kochi',
          paragraphs: [
            `We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi and Ernakulam localities. Same-day and next-day slots are often available.`,
          ],
        },
      ],
      conclusion: 'Book apartment cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Do you clean 2BHK and 3BHK apartments in Kochi?',
        answer:
          'Yes. We clean 1BHK, 2BHK, 3BHK and larger apartments. Pricing is based on size and is shown when you book online.',
      },
      {
        question: 'Which areas do you cover for apartment cleaning?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.apartment,
  },

  'villa-cleaning-kochi': {
    slug: 'villa-cleaning-kochi',
    serviceName: 'Villa Cleaning Service',
    serviceTitle: 'Villa Cleaning Kochi',
    hero: {
      eyebrow: 'Villas & independent houses',
      title: 'Villa Cleaning Service in Kochi',
      description: `Professional villa and independent house cleaning in Kochi. Deep cleaning and regular upkeep for large homes in Kakkanad, Edappally, Tripunithura and across Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Flexible slots' },
        { icon: 'Shield', text: 'Trained team' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Full villa deep cleaning',
      'Multiple bedroom & bathroom cleaning',
      'Living, kitchen & balcony',
      'Staircase & common areas',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book villa cleaning in Kochi',
      description: 'Custom quote for your villa. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Villa Cleaning Kochi | Villa Deep Cleaning Service | Tools & Bubbles',
      metaDescription: `Villa cleaning service in Kochi. Deep cleaning for villas and independent houses. Kakkanad, Edappally, Tripunithura. Call 9995216660.`,
      keywords: 'villa cleaning kochi, villa deep cleaning kochi, independent house cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles offers professional villa and independent house cleaning in Kochi. We serve Kakkanad, Edappally, Tripunithura, Aluva, Kalamassery, Angamaly and nearby areas with deep cleaning and regular upkeep.`,
        `Villas and large homes need more time and the right equipment. Our teams are trained for multi-bedroom and multi-bathroom properties, staircases, and large living areas. We give you a custom quote based on your villa size and requirements.`,
      ],
      sections: [
        {
          heading: 'What does villa cleaning include?',
          paragraphs: [
            `Villa cleaning includes full deep cleaning of all bedrooms and bathrooms, living areas, kitchen, balconies, and staircases. We use professional equipment and eco-friendly products. You can add carpet cleaning, sofa cleaning, or post-construction cleanup for a complete refresh.`,
          ],
          bulletList: [
            'Full villa deep cleaning',
            'Multiple bedrooms and bathrooms',
            'Living room, kitchen, balconies',
            'Staircase and common areas',
          ],
        },
        {
          heading: 'Where do you offer villa cleaning in Kochi?',
          paragraphs: [
            `We offer villa cleaning in Kakkanad, Edappally, Tripunithura, Kaloor, Vyttila, Palarivattom, Aluva, Kalamassery, Angamaly, and across Kochi and Ernakulam. Call 9995216660 for a custom quote.`,
          ],
        },
      ],
      conclusion: 'Book villa cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Do you clean large villas in Kochi?',
        answer:
          'Yes. We clean villas and independent houses of any size. We provide a custom quote based on bedrooms, bathrooms, and area. Call 9995216660.',
      },
      {
        question: 'Which areas do you cover for villa cleaning?',
        answer:
          'We cover Kakkanad, Edappally, Tripunithura, Kaloor, Vyttila, Palarivattom, Aluva, Kalamassery, Angamaly, and all Kochi suburbs.',
      },
    ],
    images: SERVICE_IMAGES.villa,
  },

  'move-in-cleaning-kochi': {
    slug: 'move-in-cleaning-kochi',
    serviceName: 'Move In / Move Out Cleaning',
    serviceTitle: 'Move In Move Out Cleaning Kochi',
    hero: {
      eyebrow: 'Move-in & move-out cleaning',
      title: 'Move In Move Out Cleaning in Kochi',
      description: `Professional move-in and move-out cleaning in Kochi. Handover-ready apartments and villas. Serving Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Slot as per your move' },
        { icon: 'Shield', text: 'Handover-ready' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Full interior deep cleaning',
      'Kitchen & bathroom sanitization',
      'Floor & wall cleaning',
      'Window & balcony cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book move-in/move-out cleaning in Kochi',
      description: 'Get your property handover-ready. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Move In Move Out Cleaning Kochi | Handover Cleaning | Tools & Bubbles',
      metaDescription: `Move-in and move-out cleaning in Kochi. Handover-ready apartments and villas. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'move in cleaning kochi, move out cleaning kochi, handover cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides move-in and move-out cleaning in Kochi so your apartment or villa is handover-ready. We serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery and Angamaly. Whether you are moving into a new flat or handing over to the landlord or buyer, we deep clean every room, kitchen, bathroom, floor, and window so the property is spotless.`,
        `Our move-in and move-out cleaning includes full interior deep cleaning, kitchen and bathroom sanitisation, floor and wall cleaning, and window and balcony cleaning. We schedule the visit as per your move date and use eco-friendly products. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What is included in move-in and move-out cleaning?',
          paragraphs: [
            `We perform a full deep clean so the property is ready for handover or for you to move in. This includes all rooms, kitchen degreasing, bathroom descaling, floor scrubbing, and window and balcony cleaning. We can also do post-repair or post-painting cleanup.`,
          ],
          bulletList: [
            'Full interior deep cleaning',
            'Kitchen and bathroom sanitisation',
            'Floor and wall cleaning',
            'Window and balcony cleaning',
            'Suitable for tenant handover or new move-in',
          ],
        },
        {
          heading: 'Areas we serve for move-in/move-out cleaning',
          paragraphs: [
            `We provide move-in and move-out cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby localities. We can schedule the visit around your move date.`,
          ],
        },
      ],
      conclusion:
        'Book move-in/move-out cleaning in Kochi today. Call 9995216660 or book online for handover-ready results.',
    },
    faq: [
      {
        question: 'When should I book move-out cleaning?',
        answer:
          'Book for the day you hand over the keys or the day before. We will deep clean so the property is ready for the landlord or new tenant.',
      },
      {
        question: 'Do you clean before move-in?',
        answer:
          'Yes. We clean newly rented or purchased homes so you can move in to a spotless space. Book for the day before or the day of your move.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi and Ernakulam.',
      },
    ],
    images: SERVICE_IMAGES.moveIn,
  },

  'post-construction-cleaning-kochi': {
    slug: 'post-construction-cleaning-kochi',
    usePremiumTierGuide: true,
    serviceName: 'Post Construction Cleaning',
    serviceTitle: 'Post Construction Cleaning Kochi',
    hero: {
      eyebrow: 'After renovation & construction',
      title: 'Post Construction Cleaning in Kochi',
      description: `Professional post-construction and post-renovation cleaning in Kochi. Dust, debris and paint removal. Kakkanad, Edappally, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Scheduled after handover' },
        { icon: 'Shield', text: 'Industrial-grade equipment' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Dust and debris removal',
      'Paint splatter & stain removal',
      'Floor and wall cleaning',
      'Window and fixture cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book post-construction cleaning in Kochi',
      description: 'Move-in ready after renovation. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Post Construction Cleaning Kochi | After Renovation Cleaning | Tools & Bubbles',
      metaDescription: `Post-construction and post-renovation cleaning in Kochi. Dust, debris, paint removal. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords:
        'post construction cleaning kochi, after renovation cleaning kochi, construction cleanup kochi',
      introParagraphs: [
        `Tools & Bubbles offers post-construction and post-renovation cleaning in Kochi. We remove dust, debris, and paint splatter so your space is move-in ready. Serving Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi.`,
        `After construction or renovation, floors, walls, and fixtures are covered in dust and often paint spots. Our team uses industrial-grade equipment and methods to clear debris, clean windows, and leave the property ready for furniture and occupancy. We schedule after your contractor hands over the site.`,
      ],
      sections: [
        {
          heading: 'What does post-construction cleaning include?',
          paragraphs: [
            `We focus on dust and debris removal, paint splatter and stain removal, floor and wall cleaning, and window and fixture cleaning. We do not do structural or repair work—only thorough cleaning so the space is liveable and presentable.`,
          ],
          bulletList: [
            'Dust and debris removal from all surfaces',
            'Paint splatter and stain removal',
            'Floor and wall cleaning',
            'Window and fixture cleaning',
            'Final wipe-down of cabinets and counters',
          ],
        },
        {
          heading: 'When to book post-construction cleaning',
          paragraphs: [
            `Book once construction or renovation is complete and the site is handed over. We need access to power and water. We serve Kakkanad, Edappally, Kaloor, Vyttila, and all Kochi. Call 9995216660 for a quote.`,
          ],
        },
      ],
      conclusion:
        'Book post-construction cleaning in Kochi today. Call 9995216660 or book online for a move-in ready space.',
    },
    faq: [
      {
        question: 'When should I schedule post-construction cleaning?',
        answer:
          'Schedule after your builder or contractor hands over the site. We need power and water available. We remove dust, debris, and paint residue.',
      },
      {
        question: 'Do you clean after painting?',
        answer:
          'Yes. We remove paint splatter from floors, windows, and fixtures and do a full dust and debris clean so the space is ready to use.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi and Ernakulam.',
      },
    ],
    images: SERVICE_IMAGES.postConstruction,
  },

  'residential-cleaning-studio-1bhk-kochi': {
    slug: 'residential-cleaning-studio-1bhk-kochi',
    bookingTierSlug: 'residential-cleaning-studiocompact-1bhk-300-750-sq-ft',
    usePremiumTierGuide: true,
    listingFromAmount: 4799,
    serviceName: 'Studio / 1BHK Residential Cleaning',
    serviceTitle: 'Studio / 1BHK Cleaning Kochi',
    hero: {
      eyebrow: 'Compact homes',
      title: 'Studio & 1BHK Cleaning in Kochi',
      description: `Professional deep cleaning for studio and 1BHK homes (about 300–750 sq ft). Serving Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom and all Kochi with eco-friendly products and uniformed staff.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Verified cleaners' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Kitchen degreasing & wipe-down',
      'Bathroom descaling & sanitisation',
      'Living & bedroom dusting',
      'Floors mopped & vacuumed',
      'Fans, switches & fixtures',
      'Balcony sweep (where applicable)',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book studio / 1BHK cleaning in Kochi',
      description: 'Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Studio & 1BHK Cleaning Kochi | {FromPrice} | Tools & Bubbles',
      metaDescription: `Studio and 1BHK deep cleaning in Kochi {fromPrice}. Kakkanad, Edappally, Kaloor, Vyttila. Call 9995216660.`,
      keywords: 'studio cleaning kochi, 1bhk cleaning kochi, small apartment cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides studio and 1BHK residential cleaning across Kochi and Ernakulam. Compact flats need focused kitchen and bathroom work, thorough floor care, and dusting in living and sleeping areas—we bring the right team size and checklist for 300–750 sq ft homes.`,
        `Popular areas include Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery and Angamaly. Pricing starts {fromPrice} for this tier; book online or call 9995216660 for same-day or next-day slots.`,
      ],
      sections: [
        {
          heading: 'What is included for studio / 1BHK cleaning?',
          paragraphs: [
            `We deep clean the kitchen (counters, sink, exterior of appliances), bathrooms (tiles, fixtures, mirrors), living and bedroom surfaces, and all floors. Fans, switches, and reachable fixtures are wiped. Balcony sweeping is included where accessible.`,
          ],
        },
        {
          heading: 'Why choose Tools & Bubbles for small apartments?',
          paragraphs: [
            `Our teams are trained for Indian apartment layouts, use safe products for families and pets, and offer transparent pricing before you confirm. If you are not satisfied, we re-clean the concerned areas at no extra cost.`,
          ],
        },
      ],
      conclusion: 'Book studio or 1BHK cleaning in Kochi today—call 9995216660 or use the booking form.',
    },
    faq: [
      {
        question: 'How long does studio / 1BHK cleaning take?',
        answer:
          'Typically a half-day visit depending on condition and add-ons. We confirm duration when you book.',
      },
      {
        question: 'Do you bring equipment and chemicals?',
        answer:
          'Yes. We bring professional equipment and approved cleaning agents. You only need power and water access.',
      },
    ],
    images: tierLandingImageSet('/images/hero-landing-residential-studio-1bhk.png'),
  },

  'residential-cleaning-2bhk-kochi': {
    slug: 'residential-cleaning-2bhk-kochi',
    bookingTierSlug: 'residential-cleaning-standard-1bhksmall-2bhk-750-1500-sq-ft',
    usePremiumTierGuide: true,
    listingFromAmount: 7499,
    serviceName: '2BHK Residential Cleaning',
    serviceTitle: '2BHK Cleaning Kochi',
    hero: {
      eyebrow: 'Standard apartments',
      title: '2BHK Deep Cleaning in Kochi',
      description: `Full-home deep cleaning for 2BHK homes (about 750–1500 sq ft). Ideal before festivals, after travel, or periodic refresh—Kakkanad, Edappally, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Flexible slots' },
        { icon: 'Shield', text: 'Trained team' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Two bedrooms + living deep dusting',
      'Kitchen degreasing',
      'Two bathrooms descaled',
      'All floors mopped & vacuumed',
      'Windows & balcony (reachable)',
      'Fans & fixtures',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book 2BHK cleaning in Kochi',
      description: 'Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: '2BHK Cleaning Kochi | Deep Cleaning {fromPrice} | Tools & Bubbles',
      metaDescription: `2BHK deep cleaning in Kochi {fromPrice}. Kakkanad, Edappally, Kaloor, Vyttila. Call 9995216660.`,
      keywords: '2bhk cleaning kochi, 2bhk deep cleaning kochi, apartment cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles offers 2BHK residential deep cleaning throughout Kochi. This tier covers roughly 750–1500 sq ft: two bedrooms, living and dining, kitchen, bathrooms, and all floors, with optional add-ons like sofa or carpet cleaning.`,
        `We serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly and nearby areas. Pricing starts {fromPrice}. Same-day and next-day slots are often available.`,
      ],
      sections: [
        {
          heading: 'What does 2BHK deep cleaning include?',
          paragraphs: [
            `We deep clean all rooms: dusting and wiping surfaces, kitchen degreasing, bathroom descaling, floor mopping and vacuuming, and cleaning reachable windows and balconies. Fans, switches, and light fixtures are included.`,
          ],
        },
        {
          heading: 'Can I add sofa or carpet cleaning?',
          paragraphs: [
            `Yes. You can add sofa shampooing, carpet cleaning, mattress cleaning, and other special services at booking. Our team will confirm time and pricing for add-ons.`,
          ],
        },
      ],
      conclusion: 'Book 2BHK cleaning in Kochi—call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'How many cleaners come for a 2BHK?',
        answer:
          'Usually two professionals depending on the package and condition. We assign enough staff to finish efficiently without rushing quality.',
      },
      {
        question: 'Is this suitable for tenant handover?',
        answer:
          'Yes. Many customers book this tier before handover. For empty flats you may also consider our move-in/move-out service.',
      },
    ],
    images: tierLandingImageSet('/images/hero-landing-residential-2bhk.png'),
  },

  'residential-cleaning-3bhk-kochi': {
    slug: 'residential-cleaning-3bhk-kochi',
    bookingTierSlug: 'residential-cleaning-standard-2bhksmall-3bhk-1500-2000-sq-ft',
    listingFromAmount: 9499,
    serviceName: '3BHK Residential Cleaning',
    serviceTitle: '3BHK Cleaning Kochi',
    hero: {
      eyebrow: 'Larger apartments',
      title: '3BHK Deep Cleaning in Kochi',
      description: `Complete deep cleaning for 3BHK homes (about 1500–2000 sq ft). Extra bedrooms and living space get the same thorough treatment—serving all major Kochi localities.`,
      highlights: [
        { icon: 'Clock', text: 'Team deployment' },
        { icon: 'Shield', text: 'Supervised quality' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Three bedrooms + common areas',
      'Kitchen & utility focus',
      'Three bathrooms sanitised',
      'Full floor care',
      'Windows (reachable) & balconies',
      'Fans, lights & woodwork wipe',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book 3BHK cleaning in Kochi',
      description: 'Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: '3BHK Cleaning Kochi | Deep Cleaning {fromPrice} | Tools & Bubbles',
      metaDescription: `3BHK deep cleaning in Kochi {fromPrice}. Kakkanad, Edappally, Vyttila, Kaloor. Call 9995216660.`,
      keywords: '3bhk cleaning kochi, 3bhk deep cleaning kochi, large apartment cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides 3BHK deep cleaning for families in Kochi. This package is sized for roughly 1500–2000 sq ft with three bedrooms, multiple bathrooms, kitchen, utility, and living areas.`,
        `We deploy a trained team so the job is done in one visit where possible. Areas covered include Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery and Angamaly. {FromPrice}.`,
      ],
      sections: [
        {
          heading: 'What is covered in 3BHK cleaning?',
          paragraphs: [
            `Every bedroom, living and dining area, kitchen, and bathroom receives deep cleaning: dust removal, degreasing, descaling, floor care, and fixture cleaning. We coordinate team size for larger layouts.`,
          ],
        },
        {
          heading: 'Do you clean balconies and utility areas?',
          paragraphs: [
            `Yes, reachable balconies and utility areas are included in the standard checklist unless otherwise noted at booking.`,
          ],
        },
      ],
      conclusion: 'Book 3BHK cleaning in Kochi today—9995216660 or online booking.',
    },
    faq: [
      {
        question: 'How long does a 3BHK deep clean take?',
        answer:
          'Often a full day for a thorough clean depending on furniture, condition, and add-ons. We confirm an estimate when you book.',
      },
      {
        question: 'Can I split the work across two days?',
        answer: 'For special cases, call 9995216660 and our team will suggest options.',
      },
    ],
    images: tierLandingImageSet('/images/hero-landing-residential-3bhk.png'),
  },

  'residential-cleaning-4-5bhk-villa-kochi': {
    slug: 'residential-cleaning-4-5bhk-villa-kochi',
    bookingTierSlug: 'residential-cleaning-4-5-bhk-villas-2500-3000-sq-ft',
    usePremiumTierGuide: true,
    listingFromAmount: 12499,
    serviceName: '4–5 BHK & Villa Cleaning',
    serviceTitle: '4–5 BHK & Villa Cleaning Kochi',
    hero: {
      eyebrow: 'Villas & large homes',
      title: '4–5 BHK & Villa Cleaning in Kochi',
      description: `Deep cleaning for large homes and villas (about 2500–3000 sq ft). Staircases, multiple baths, and spacious living areas—handled by an experienced crew across Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Planned visits' },
        { icon: 'Shield', text: 'Large-home experts' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'All bedrooms & living zones',
      'Multiple bathrooms & powder rooms',
      'Kitchen & pantry deep clean',
      'Staircase & railings (reachable)',
      'Floor scrubbing / vacuuming',
      'Windows & main doors (reachable)',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book villa / 4–5 BHK cleaning',
      description: 'Call 9995216660 or book online.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: '4–5 BHK & Villa Cleaning Kochi | {FromPrice} | Tools & Bubbles',
      metaDescription: `Villa and 4–5 BHK deep cleaning in Kochi {fromPrice}. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'villa cleaning kochi, 5bhk cleaning kochi, large home deep cleaning kochi',
      introParagraphs: [
        `Large villas and 4–5 BHK homes in Kochi need more time, equipment, and coordination. Tools & Bubbles assigns experienced teams for roughly 2500–3000 sq ft properties, including staircases, multiple wet areas, and expansive living spaces.`,
        `We serve premium residential pockets across Kakkanad, Edappally, Kaloor, Vyttila, Tripunithura, Aluva and surrounding areas. {FromPrice} for this tier; custom quotes available for unusually large layouts.`,
      ],
      sections: [
        {
          heading: 'What makes villa cleaning different?',
          paragraphs: [
            `Larger floor plates, high ceilings, and more bathrooms mean we bring additional staff and sometimes extra equipment. We plan the sequence so wet areas, kitchens, and bedrooms are cleaned systematically.`,
          ],
        },
        {
          heading: 'Do you clean outdoor patios?',
          paragraphs: [
            `Basic sweeping of accessible patios may be included; heavy exterior pressure washing is quoted separately. Mention your layout when booking.`,
          ],
        },
      ],
      conclusion: 'Book villa or 4–5 BHK cleaning in Kochi—call 9995216660.',
    },
    faq: [
      {
        question: 'Is chandelier or high ceiling cleaning included?',
        answer:
          'Only reachable heights with our standard equipment. Very high fixtures may need a separate quote.',
      },
      {
        question: 'Can you clean before a housewarming?',
        answer:
          'Yes. Many clients book this tier before housewarming or move-in. Tell us your date when booking.',
      },
    ],
    images: tierLandingImageSet('/images/hero-landing-residential-villa.png'),
  },

  'commercial-cleaning-up-to-500-sq-ft-kochi': {
    slug: 'commercial-cleaning-up-to-500-sq-ft-kochi',
    bookingTierSlug: 'commercial-cleaning-0-500-sq-ft',
    usePremiumTierGuide: true,
    listingFromAmount: 4799,
    serviceName: 'Commercial Cleaning (Up to 500 sq ft)',
    serviceTitle: 'Small Office Cleaning Kochi',
    hero: {
      eyebrow: 'Offices & retail',
      title: 'Commercial Cleaning up to 500 sq ft',
      description: `Professional cleaning for small offices, clinics, and retail units up to 500 sq ft in Kochi. Reception, work floor, and washroom focus with flexible timing.`,
      highlights: [
        { icon: 'Clock', text: 'After-hours option' },
        { icon: 'Shield', text: 'Corporate-ready' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Work floor dusting & mopping',
      'Washroom sanitisation',
      'Reception & glass (reachable)',
      'Pantry wipe-down',
      'Bin liner change',
      'Disinfection of high-touch points',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Infopark',
      'Aluva',
      'Kalamassery',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book small commercial cleaning',
      description: 'Call 9995216660 or book online.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Small Office Cleaning Kochi | Up to 500 sq ft {fromPrice} | Tools & Bubbles',
      metaDescription: `Commercial cleaning for offices up to 500 sq ft in Kochi {fromPrice}. Edappally, Kakkanad, Vyttila. Call 9995216660.`,
      keywords: 'small office cleaning kochi, commercial cleaning kochi 500 sq ft, retail cleaning kochi',
      introParagraphs: [
        `Small offices and retail counters in Kochi benefit from focused daily or periodic cleaning. This tier covers up to 500 sq ft: work area, reception, pantry, and restroom sanitisation.`,
        `We serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Infopark and nearby commercial pockets. {FromPrice}. We can schedule after office hours on request.`,
      ],
      sections: [
        {
          heading: 'What is included for up to 500 sq ft?',
          paragraphs: [
            `Dusting and mopping the customer area, sanitising restrooms, wiping reception and glass (within reach), pantry cleanup, and disinfecting door handles and switches.`,
          ],
        },
        {
          heading: 'Do you provide regular contracts?',
          paragraphs: [
            `Yes. Call 9995216660 for weekly or monthly contracts for small offices. One-time deep cleans are also available.`,
          ],
        },
      ],
      conclusion: 'Book commercial cleaning up to 500 sq ft in Kochi today.',
    },
    faq: [
      {
        question: 'Is GST included?',
        answer: 'Pricing is discussed at booking. We provide a clear quote before you confirm.',
      },
      {
        question: 'Do you bring your own supplies?',
        answer:
          'Yes, we bring standard commercial cleaning supplies unless your facility requires specific approved chemicals.',
      },
    ],
    images: tierLandingImageSet('/images/hero-landing-commercial-500.png'),
  },

  'commercial-cleaning-500-1000-sq-ft-kochi': {
    slug: 'commercial-cleaning-500-1000-sq-ft-kochi',
    bookingTierSlug: 'commercial-cleaning-500-1000-sq-ft',
    usePremiumTierGuide: true,
    listingFromAmount: 8499,
    serviceName: 'Commercial Cleaning (500–1000 sq ft)',
    serviceTitle: 'Office Cleaning 500–1000 sq ft Kochi',
    hero: {
      eyebrow: 'Growing teams',
      title: 'Commercial Cleaning 500–1000 sq ft',
      description: `Open-plan offices, showrooms, and clinics in the mid-size range. Consistent standards for floors, workstations, and shared spaces across Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Flexible schedule' },
        { icon: 'Shield', text: 'Trained crew' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Open floor vacuum & mop',
      'Workstations & desks (exterior)',
      'Meeting room refresh',
      'Restrooms & pantry',
      'Glass partitions (reachable)',
      'Waste disposal points',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Infopark',
      'SmartCity',
      'Aluva',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book 500–1000 sq ft commercial cleaning',
      description: 'Call 9995216660 or book online.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Office Cleaning 500–1000 sq ft Kochi | {FromPrice} | Tools & Bubbles',
      metaDescription: `Commercial cleaning for 500–1000 sq ft in Kochi {fromPrice}. Kakkanad, Infopark, Edappally. Call 9995216660.`,
      keywords: 'office cleaning kochi 1000 sq ft, commercial cleaning kochi, infopark office cleaning',
      introParagraphs: [
        `Mid-size offices in Kochi need reliable floor care, desk-area dusting, and clean shared facilities. This tier is designed for roughly 500–1000 sq ft commercial layouts.`,
        `We work in Kakkanad, Edappally, Kaloor, Vyttila, Infopark, SmartCity and surrounding business areas. {FromPrice}. Ask about recurring contracts.`,
      ],
      sections: [
        {
          heading: 'Typical checklist for 500–1000 sq ft',
          paragraphs: [
            `Vacuuming and mopping main floors, wiping meeting tables, sanitising restrooms and pantry, emptying bins where agreed, and cleaning reachable glass and partitions.`,
          ],
        },
        {
          heading: 'IT parks and access procedures',
          paragraphs: [
            `Our staff are used to Infopark and IT building entry norms. Share gate pass or escort requirements when booking.`,
          ],
        },
      ],
      conclusion: 'Book 500–1000 sq ft commercial cleaning in Kochi—9995216660.',
    },
    faq: [
      {
        question: 'Can you clean only on Sundays?',
        answer: 'Yes, we can schedule weekends or after hours when your office is closed.',
      },
      {
        question: 'Do you clean server rooms?',
        answer:
          'No. We avoid active server or electrical rooms unless explicitly agreed with facility guidelines.',
      },
    ],
    images: tierLandingImageSet('/images/hero-landing-commercial-500-1000.png'),
  },

  'commercial-cleaning-1000-1500-sq-ft-kochi': {
    slug: 'commercial-cleaning-1000-1500-sq-ft-kochi',
    bookingTierSlug: 'commercial-cleaning-1001-1500-sq-ft',
    usePremiumTierGuide: true,
    listingFromAmount: 9499,
    serviceName: 'Commercial Cleaning (1000–1500 sq ft)',
    serviceTitle: 'Office Cleaning 1000–1500 sq ft Kochi',
    hero: {
      eyebrow: 'Larger floors',
      title: 'Commercial Cleaning 1000–1500 sq ft',
      description: `Showrooms, branch offices, and multi-zone layouts. Extra coverage for customer-facing areas and staff zones in Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Team visit' },
        { icon: 'Shield', text: 'Quality checks' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Multi-zone floor care',
      'Customer area presentation',
      'Staff desks & common tables',
      'Multiple restrooms',
      'Pantry deep wipe',
      'Entrance & lobby polish',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Infopark',
      'Aluva',
      'Kalamassery',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book 1000–1500 sq ft cleaning',
      description: 'Call 9995216660 or book online.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Office Cleaning 1000–1500 sq ft Kochi | {FromPrice} | Tools & Bubbles',
      metaDescription: `Commercial cleaning 1000–1500 sq ft Kochi {fromPrice}. Showrooms and offices. Call 9995216660.`,
      keywords: 'showroom cleaning kochi, large office cleaning kochi, commercial cleaning 1500 sq ft',
      introParagraphs: [
        `Larger commercial units need coordinated teams and clear zones—customer areas, back office, restrooms, and pantry. This tier targets about 1000–1500 sq ft in Kochi.`,
        `We serve retail showrooms, branch offices, and professional firms in Kakkanad, Edappally, Vyttila, Kaloor, Infopark and nearby. {FromPrice}.`,
      ],
      sections: [
        {
          heading: 'What businesses use this tier?',
          paragraphs: [
            `Showrooms, dental or clinic waiting areas with back office, and medium open-plan offices. We align the checklist with your foot traffic and hygiene expectations.`,
          ],
        },
        {
          heading: 'Custom quotes',
          paragraphs: [
            `If your layout is unusual or includes multiple floors, call 9995216660 for a quick survey-based quote.`,
          ],
        },
      ],
      conclusion: 'Book 1000–1500 sq ft commercial cleaning in Kochi today.',
    },
    faq: [
      {
        question: 'How often should we book?',
        answer:
          'High-traffic showrooms may need weekly cleaning; offices often choose weekly or bi-weekly. We recommend a schedule after the first visit.',
      },
    ],
    images: tierLandingImageSet('/images/hero-landing-commercial-1000-1500.png'),
  },

  'commercial-cleaning-1500-plus-sq-ft-kochi': {
    slug: 'commercial-cleaning-1500-plus-sq-ft-kochi',
    bookingTierSlug: 'commercial-cleaning-1501-2500-sq-ft',
    usePremiumTierGuide: true,
    listingFromAmount: 12499,
    serviceName: 'Commercial Cleaning (1500+ sq ft)',
    serviceTitle: 'Large Commercial Cleaning Kochi',
    hero: {
      eyebrow: 'Large spaces',
      title: 'Commercial Cleaning 1500+ sq ft',
      description: `Spacious offices, retail floors, and multi-cabin layouts. Scalable crew size and equipment for 1501 sq ft and above in Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Project planning' },
        { icon: 'Shield', text: 'Supervised teams' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Wide-area floor machines / mop',
      'Multiple cabins & bays',
      'All restrooms & pantries',
      'Lobby & reception deep clean',
      'Glass & facades (reachable)',
      'Scheduled quality walkthrough',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Infopark',
      'SmartCity',
      'Aluva',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book large commercial cleaning',
      description: 'Call 9995216660 or book online.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Large Office Cleaning Kochi | 1500+ sq ft {fromPrice} | Tools & Bubbles',
      metaDescription: `Commercial cleaning for 1500+ sq ft in Kochi {fromPrice}. Large offices and retail. Call 9995216660.`,
      keywords:
        'large office cleaning kochi, commercial cleaning large space kochi, retail floor cleaning kochi',
      introParagraphs: [
        `Large commercial footprints in Kochi need planned crew deployment, equipment for wide floors, and systematic restroom and pantry coverage. This tier starts {fromPrice} for 1501 sq ft and above (exact scope confirmed at booking).`,
        `We support businesses in Kakkanad, Edappally, Vyttila, Kaloor, Infopark, SmartCity, Aluva, Angamaly and across Ernakulam. Facility managers can request recurring schedules.`,
      ],
      sections: [
        {
          heading: 'How we scope 1500+ sq ft jobs',
          paragraphs: [
            `We confirm approximate carpet vs hard floor mix, number of restrooms, pantry count, and any restricted zones. Then we assign team size and duration for a thorough clean.`,
          ],
        },
        {
          heading: 'Contracts and SLAs',
          paragraphs: [
            `For recurring large-site cleaning, call 9995216660 to discuss SLAs, attendance windows, and escalation contacts.`,
          ],
        },
      ],
      conclusion: 'Book large commercial cleaning in Kochi—call 9995216660.',
    },
    faq: [
      {
        question: 'Do you clean warehouses?',
        answer: 'We have a separate warehouse cleaning offering; mention your site type when you call.',
      },
      {
        question: 'Can you work night shifts?',
        answer:
          'Yes, night and early-morning slots are available for commercial clients where building rules allow.',
      },
    ],
    images: tierLandingImageSet('/images/hero-landing-commercial-1500-plus.png'),
  },

  'sofa-cleaning-kochi': {
    slug: 'sofa-cleaning-kochi',
    serviceName: 'Sofa Cleaning Service',
    serviceTitle: 'Sofa Cleaning Kochi',
    hero: {
      eyebrow: 'Sofa & upholstery cleaning',
      title: 'Sofa Cleaning Service in Kochi',
      description: `Professional sofa and upholstery cleaning in Kochi. Deep shampooing, stain removal. Serving Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Safe for fabric' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Sofa shampooing & deep cleaning',
      'Stain removal',
      'Fabric & leather care',
      'Odour removal',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book sofa cleaning in Kochi today',
      description: 'Professional sofa shampooing. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Sofa Cleaning Kochi | Sofa Shampooing Service | Tools & Bubbles',
      metaDescription: `Professional sofa cleaning in Kochi. Sofa shampooing, stain removal. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'sofa cleaning kochi, sofa shampooing kochi, upholstery cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides professional sofa and upholstery cleaning in Kochi. We offer deep shampooing, stain removal, and odour control for fabric and leather sofas. Serving Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery and Angamaly.`,
        `Sofas collect dust, allergens, and stains over time. Our team uses safe, fabric-friendly methods and equipment to deep clean your sofa so it looks and smells fresh. We can clean single seater, two-seater, three-seater, and sectional sofas. Same-day slots are often available. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What is included in sofa cleaning?',
          paragraphs: [
            `Our sofa cleaning includes deep shampooing, stain removal, fabric or leather care, and odour removal. We use products that are safe for most upholstery types. For leather, we use appropriate cleaners to avoid damage.`,
          ],
          bulletList: [
            'Sofa shampooing and deep cleaning',
            'Stain removal',
            'Fabric and leather care',
            'Odour removal',
            'Safe for most upholstery types',
          ],
        },
        {
          heading: 'Where do you provide sofa cleaning in Kochi?',
          paragraphs: [
            `We provide sofa cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby areas.`,
          ],
        },
      ],
      conclusion:
        'Book sofa cleaning in Kochi today. Call 9995216660 or book online for professional sofa shampooing.',
    },
    faq: [
      {
        question: 'Do you clean leather sofas?',
        answer:
          'Yes. We clean both fabric and leather sofas using appropriate products. Leather gets a gentler treatment to avoid damage.',
      },
      {
        question: 'How long does sofa cleaning take?',
        answer:
          'Typically 1–2 hours depending on the size and number of pieces. We will give an estimate when you book.',
      },
      {
        question: 'Which areas do you cover for sofa cleaning?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.sofa,
  },

  'carpet-cleaning-kochi': {
    slug: 'carpet-cleaning-kochi',
    serviceName: 'Carpet Cleaning Service',
    serviceTitle: 'Carpet Cleaning Kochi',
    hero: {
      eyebrow: 'Carpet & rug cleaning',
      title: 'Carpet Cleaning Service in Kochi',
      description: `Professional carpet and rug cleaning in Kochi. Deep extraction, stain removal. Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Safe methods' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Carpet deep cleaning',
      'Stain & odour removal',
      'Rug cleaning',
      'Dry & steam options',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book carpet cleaning in Kochi',
      description: 'Professional carpet and rug cleaning. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Carpet Cleaning Kochi | Carpet Deep Cleaning Service | Tools & Bubbles',
      metaDescription: `Carpet cleaning service in Kochi. Deep cleaning, stain removal. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'carpet cleaning kochi, rug cleaning kochi, carpet deep cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles offers professional carpet and rug cleaning in Kochi. We serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly and all major areas with deep extraction and stain removal.`,
        `Carpets and rugs trap dust, allergens, and odours. Our deep cleaning removes dirt from the pile, treats stains, and leaves carpets fresh. We use dry and steam options depending on the material. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What does carpet cleaning include?',
          paragraphs: [
            `We offer carpet deep cleaning, stain and odour removal, and rug cleaning. We use methods suitable for your carpet type and can discuss dry vs steam cleaning when you book.`,
          ],
          bulletList: [
            'Carpet deep cleaning',
            'Stain and odour removal',
            'Rug cleaning',
            'Dry and steam options where suitable',
          ],
        },
        {
          heading: 'Areas we serve for carpet cleaning',
          paragraphs: [
            `We provide carpet and rug cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby localities.`,
          ],
        },
      ],
      conclusion: 'Book carpet cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Do you clean rugs as well?',
        answer:
          'Yes. We clean both wall-to-wall carpets and rugs. Pricing may vary by size. Call 9995216660 for a quote.',
      },
      {
        question: 'How long does carpet cleaning take to dry?',
        answer:
          'Drying time depends on the method and carpet. We will advise when we finish. Usually a few hours to a day.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.carpet,
  },

  'mattress-cleaning-kochi': {
    slug: 'mattress-cleaning-kochi',
    serviceName: 'Mattress Cleaning Service',
    serviceTitle: 'Mattress Cleaning Kochi',
    hero: {
      eyebrow: 'Mattress deep cleaning',
      title: 'Mattress Cleaning Service in Kochi',
      description: `Professional mattress cleaning in Kochi. Dust mite removal, sanitization. Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Hypoallergenic' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Mattress deep cleaning',
      'Dust mite & allergen reduction',
      'Stain removal',
      'Sanitization & odour control',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book mattress cleaning in Kochi',
      description: 'Deep mattress cleaning. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Mattress Cleaning Kochi | Mattress Deep Cleaning | Tools & Bubbles',
      metaDescription: `Mattress cleaning service in Kochi. Dust mite removal, sanitization. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'mattress cleaning kochi, mattress deep cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides professional mattress cleaning in Kochi. We reduce dust mites and allergens and sanitise your mattress. Serving Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi.`,
        `Mattresses accumulate sweat, dead skin, dust mites, and allergens over time. Our deep cleaning and sanitisation help improve sleep hygiene and reduce odours. We use hypoallergenic methods and safe products. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What does mattress cleaning include?',
          paragraphs: [
            `We perform mattress deep cleaning, dust mite and allergen reduction, stain removal, and sanitisation and odour control. We do not use harsh chemicals that could harm the mattress or your health.`,
          ],
          bulletList: [
            'Mattress deep cleaning',
            'Dust mite and allergen reduction',
            'Stain removal',
            'Sanitisation and odour control',
          ],
        },
        {
          heading: 'Where do you offer mattress cleaning in Kochi?',
          paragraphs: [
            `We offer mattress cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby areas.`,
          ],
        },
      ],
      conclusion: 'Book mattress cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Is mattress cleaning safe for allergies?',
        answer:
          'Yes. We use hypoallergenic methods and focus on reducing dust mites and allergens. Our products are safe for most mattress types.',
      },
      {
        question: 'How often should I get my mattress cleaned?',
        answer:
          'Every 6–12 months is a good interval for deep cleaning, especially if you have allergies or asthma.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.bedroom,
  },

  'kitchen-cleaning-kochi': {
    slug: 'kitchen-cleaning-kochi',
    serviceName: 'Kitchen Deep Cleaning',
    serviceTitle: 'Kitchen Cleaning Kochi',
    hero: {
      eyebrow: 'Kitchen deep cleaning',
      title: 'Kitchen Deep Cleaning Service in Kochi',
      description: `Professional kitchen deep cleaning in Kochi. Degreasing, chimney, cabinets. Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Eco-friendly products' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Kitchen degreasing',
      'Chimney & exhaust cleaning',
      'Cabinet & counter cleaning',
      'Appliance exterior cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book kitchen deep cleaning in Kochi',
      description: 'Grease-free kitchen. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Kitchen Deep Cleaning Kochi | Kitchen Cleaning Service | Tools & Bubbles',
      metaDescription: `Kitchen deep cleaning in Kochi. Degreasing, chimney, cabinets. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'kitchen deep cleaning kochi, kitchen cleaning kochi, chimney cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles offers professional kitchen deep cleaning in Kochi. We degrease surfaces, clean chimneys and cabinets, and sanitise countertops and appliance exteriors. Serving Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi.`,
        `Kitchen grease and grime build up on backsplashes, exhaust filters, and cabinets. Our team uses effective, eco-friendly products to cut through grease and leave your kitchen clean and hygienic. We also clean chimney filters and exterior surfaces of appliances. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What is included in kitchen deep cleaning?',
          paragraphs: [
            `We perform kitchen degreasing, chimney and exhaust cleaning, cabinet and counter cleaning, and appliance exterior cleaning. We do not dismantle or repair appliances—only thorough cleaning of accessible surfaces.`,
          ],
          bulletList: [
            'Kitchen degreasing (countertops, backsplash)',
            'Chimney and exhaust filter cleaning',
            'Cabinet and counter cleaning',
            'Appliance exterior cleaning',
          ],
        },
        {
          heading: 'Areas we serve for kitchen cleaning in Kochi',
          paragraphs: [
            `We provide kitchen deep cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby localities.`,
          ],
        },
      ],
      conclusion: 'Book kitchen deep cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Do you clean chimney and exhaust?',
        answer:
          'Yes. We clean chimney and exhaust filters and accessible duct areas. This helps improve airflow and reduce grease buildup.',
      },
      {
        question: 'Which areas do you cover for kitchen cleaning?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.kitchen,
  },

  'bathroom-cleaning-kochi': {
    slug: 'bathroom-cleaning-kochi',
    serviceName: 'Bathroom Deep Cleaning',
    serviceTitle: 'Bathroom Cleaning Kochi',
    hero: {
      eyebrow: 'Bathroom deep cleaning',
      title: 'Bathroom Deep Cleaning Service in Kochi',
      description: `Professional bathroom deep cleaning in Kochi. Descaling, sanitization, tile cleaning. Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Sanitized & safe' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Bathroom descaling',
      'Tile & grout cleaning',
      'Sanitization',
      'Fixture & mirror cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book bathroom deep cleaning in Kochi',
      description: 'Spotless, sanitized bathrooms. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Bathroom Deep Cleaning Kochi | Bathroom Cleaning Service | Tools & Bubbles',
      metaDescription: `Bathroom deep cleaning in Kochi. Descaling, sanitization, tile cleaning. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'bathroom deep cleaning kochi, bathroom cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides professional bathroom deep cleaning in Kochi. We descale taps and tiles, sanitise toilets and basins, and clean grout and fixtures. Serving Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi.`,
        `Bathrooms need regular deep cleaning to remove scale, soap scum, and germs. Our team uses effective, safe products to descale, sanitise, and leave tiles, mirrors, and fixtures spotless. We clean one or multiple bathrooms in a single visit. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What does bathroom deep cleaning include?',
          paragraphs: [
            `We perform bathroom descaling, tile and grout cleaning, sanitisation of toilet and basin, and fixture and mirror cleaning. We use products that remove scale and soap scum without damaging surfaces.`,
          ],
          bulletList: [
            'Bathroom descaling (taps, tiles)',
            'Tile and grout cleaning',
            'Sanitisation (toilet, basin)',
            'Fixture and mirror cleaning',
          ],
        },
        {
          heading: 'Where do you provide bathroom cleaning in Kochi?',
          paragraphs: [
            `We provide bathroom deep cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby areas.`,
          ],
        },
      ],
      conclusion: 'Book bathroom deep cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Do you clean multiple bathrooms?',
        answer:
          'Yes. We can clean one or multiple bathrooms in a single visit. Pricing can depend on the number of bathrooms.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Marine Drive, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.bathroom,
  },

  'commercial-cleaning-kochi': {
    slug: 'commercial-cleaning-kochi',
    serviceName: 'Commercial Cleaning Service',
    serviceTitle: 'Commercial Cleaning Kochi',
    hero: {
      eyebrow: 'Offices, showrooms, restaurants',
      title: 'Commercial Cleaning Service in Kochi',
      description: `Professional commercial cleaning in Kochi. Offices, showrooms, restaurants, clinics, warehouses. Kakkanad, Infopark, Edappally, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Flexible timings' },
        { icon: 'Shield', text: 'Contract & one-time' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Office & workstation cleaning',
      'Reception & common area cleaning',
      'Restroom sanitization',
      'Floor & window cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Infopark',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book commercial cleaning in Kochi',
      description: 'Contracts and one-time deep cleans. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Commercial Cleaning Kochi | Office & Showroom Cleaning | Tools & Bubbles',
      metaDescription: `Commercial cleaning in Kochi. Offices, showrooms, restaurants. Infopark, Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'commercial cleaning kochi, office cleaning kochi, showroom cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles offers professional commercial cleaning in Kochi for offices, showrooms, restaurants, clinics, and warehouses. We serve Infopark, Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi.`,
        `We provide both one-time deep cleans and contract cleaning for businesses. Our teams work before or after office hours so your operations are not disrupted. We clean workstations, pantries, restrooms, reception areas, and floors. Contact us for a custom quote or book a one-time clean online. Call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What does commercial cleaning include?',
          paragraphs: [
            `Commercial cleaning covers office and workstation cleaning, reception and common area cleaning, restroom sanitisation, and floor and window cleaning. We can tailor the scope for your space and frequency (daily, weekly, or one-time).`,
          ],
          bulletList: [
            'Office and workstation cleaning',
            'Reception and common area cleaning',
            'Restroom sanitisation',
            'Floor and window cleaning',
            'Contract or one-time options',
          ],
        },
        {
          heading: 'Where do you provide commercial cleaning in Kochi?',
          paragraphs: [
            `We provide commercial cleaning across Kochi and Ernakulam, including Infopark, Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, and Angamaly.`,
          ],
        },
      ],
      conclusion:
        'Book commercial cleaning in Kochi today. Call 9995216660 or book online for contracts or one-time deep cleans.',
    },
    faq: [
      {
        question: 'Do you offer contract cleaning for offices?',
        answer:
          'Yes. We offer daily, weekly, or custom-frequency contract cleaning for offices in Kochi. Call 9995216660 to discuss your requirements.',
      },
      {
        question: 'Can you clean after office hours?',
        answer: 'Yes. We can schedule before or after business hours so your team is not disturbed.',
      },
      {
        question: 'Which areas do you cover for commercial cleaning?',
        answer:
          'We cover Infopark, Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.office,
  },

  'office-cleaning-kochi': {
    slug: 'office-cleaning-kochi',
    serviceName: 'Office Cleaning Service',
    serviceTitle: 'Office Cleaning Kochi',
    hero: {
      eyebrow: 'Office & workspace cleaning',
      title: 'Office Cleaning Service in Kochi',
      description: `Professional office cleaning in Kochi. Daily, weekly or deep cleaning. Infopark, Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Before/after hours' },
        { icon: 'Shield', text: 'NDA-friendly' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Workstation & desk cleaning',
      'Pantry & restroom cleaning',
      'Reception & lobby',
      'Floor & vacuum cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Infopark',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book office cleaning in Kochi',
      description: 'Daily or deep office cleaning. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Office Cleaning Kochi | Office Deep Cleaning | Tools & Bubbles',
      metaDescription: `Office cleaning service in Kochi. Infopark, Kakkanad, Edappally, Vyttila. Daily & deep cleaning. Call 9995216660.`,
      keywords: 'office cleaning kochi, office deep cleaning kochi, office cleaning service kochi',
      introParagraphs: [
        `Tools & Bubbles provides professional office cleaning in Kochi. We serve Infopark, Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi with daily, weekly, or one-time deep cleaning.`,
        `We clean workstations, pantries, restrooms, reception, and floors. Our teams are used to working in IT parks and corporate buildings and can work before or after office hours. We respect confidentiality and can sign NDAs where required. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What is included in office cleaning?',
          paragraphs: [
            `Office cleaning includes workstation and desk cleaning, pantry and restroom cleaning, reception and lobby cleaning, and floor and vacuum cleaning. We can do daily or weekly upkeep or a one-time deep clean.`,
          ],
          bulletList: [
            'Workstation and desk cleaning',
            'Pantry and restroom cleaning',
            'Reception and lobby',
            'Floor and vacuum cleaning',
            'Before/after hours slots',
          ],
        },
        {
          heading: 'Office cleaning in Infopark and Kochi',
          paragraphs: [
            `We regularly clean offices in Infopark, Kakkanad, Edappally, Kaloor, Vyttila, and across Kochi. Many IT companies and startups use us for daily or weekly contract cleaning. Call 9995216660 for a quote.`,
          ],
        },
      ],
      conclusion:
        'Book office cleaning in Kochi today. Call 9995216660 or book online for daily, weekly, or deep cleaning.',
    },
    faq: [
      {
        question: 'Do you clean offices in Infopark?',
        answer:
          'Yes. We clean offices in Infopark Phase 1, Phase 2, SmartCity, and across Kakkanad. We can work before or after office hours.',
      },
      {
        question: 'Can you sign an NDA?',
        answer: 'Yes. We respect confidentiality and can sign NDAs where required for corporate clients.',
      },
      {
        question: 'Which areas do you cover for office cleaning?',
        answer:
          'We cover Infopark, Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.office,
  },

  'showroom-cleaning-kochi': {
    slug: 'showroom-cleaning-kochi',
    serviceName: 'Showroom Cleaning Service',
    serviceTitle: 'Showroom Cleaning Kochi',
    hero: {
      eyebrow: 'Showroom & retail cleaning',
      title: 'Showroom Cleaning Service in Kochi',
      description: `Professional showroom cleaning in Kochi. Retail spaces, display areas. Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Flexible timings' },
        { icon: 'Shield', text: 'Spotless display' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Floor & display cleaning',
      'Glass & mirror cleaning',
      'Dusting & vacuuming',
      'Restroom sanitization',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book showroom cleaning in Kochi',
      description: 'Keep your showroom spotless. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Showroom Cleaning Kochi | Showroom Cleaning Service | Tools & Bubbles',
      metaDescription: `Showroom cleaning in Kochi. Retail and display cleaning. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'showroom cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles offers professional showroom cleaning in Kochi. We serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi for retail and display spaces.`,
        `Showrooms need spotless floors, clean glass, and dust-free displays to make a good impression. Our team cleans floors, display areas, glass and mirrors, and restrooms. We can schedule during or after business hours. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What does showroom cleaning include?',
          paragraphs: [
            `We clean floors and display areas, glass and mirrors, dust and vacuum, and sanitise restrooms. We can work around your opening hours or after closing.`,
          ],
          bulletList: [
            'Floor and display cleaning',
            'Glass and mirror cleaning',
            'Dusting and vacuuming',
            'Restroom sanitisation',
          ],
        },
        {
          heading: 'Where do you provide showroom cleaning?',
          paragraphs: [
            `We provide showroom cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby areas.`,
          ],
        },
      ],
      conclusion: 'Book showroom cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Can you clean after we close?',
        answer:
          'Yes. We can schedule cleaning after your showroom closes so the space is ready for the next day.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.showroom,
  },

  'restaurant-cleaning-kochi': {
    slug: 'restaurant-cleaning-kochi',
    serviceName: 'Restaurant Cleaning Service',
    serviceTitle: 'Restaurant Cleaning Kochi',
    hero: {
      eyebrow: 'Restaurant & kitchen cleaning',
      title: 'Restaurant Cleaning Service in Kochi',
      description: `Professional restaurant and kitchen cleaning in Kochi. Deep cleaning, grease removal. Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'After hours' },
        { icon: 'Shield', text: 'Food-safe' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Kitchen degreasing',
      'Exhaust & duct cleaning',
      'Dining area cleaning',
      'Restroom sanitization',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book restaurant cleaning in Kochi',
      description: 'Deep kitchen and dining cleaning. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Restaurant Cleaning Kochi | Restaurant Kitchen Cleaning | Tools & Bubbles',
      metaDescription: `Restaurant cleaning in Kochi. Kitchen degreasing, exhaust cleaning. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'restaurant cleaning kochi, restaurant kitchen cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides professional restaurant and kitchen cleaning in Kochi. We degrease kitchens, clean exhaust and ducts, and sanitise dining and restroom areas. Serving Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi.`,
        `Restaurant kitchens build up grease and grime quickly. Our team uses food-safe methods and products to deep clean kitchen surfaces, exhaust systems, and dining areas. We typically work after closing hours. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What does restaurant cleaning include?',
          paragraphs: [
            `We perform kitchen degreasing, exhaust and duct cleaning, dining area cleaning, and restroom sanitisation. We use products that are safe for food-service environments.`,
          ],
          bulletList: [
            'Kitchen degreasing',
            'Exhaust and duct cleaning',
            'Dining area cleaning',
            'Restroom sanitisation',
            'After-hours scheduling',
          ],
        },
        {
          heading: 'Where do you provide restaurant cleaning in Kochi?',
          paragraphs: [
            `We provide restaurant cleaning across Kochi and Ernakulam. We work with cafes, restaurants, and cloud kitchens in Kakkanad, Edappally, Kaloor, Vyttila, and all major localities.`,
          ],
        },
      ],
      conclusion: 'Book restaurant cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Do you clean after closing time?',
        answer:
          'Yes. We usually schedule restaurant kitchen cleaning after your closing time so we do not disturb service.',
      },
      {
        question: 'Are your products safe for food areas?',
        answer:
          'Yes. We use products that are safe for food-service environments and follow hygiene best practices.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.restaurant,
  },

  'clinic-cleaning-kochi': {
    slug: 'clinic-cleaning-kochi',
    serviceName: 'Clinic Cleaning Service',
    serviceTitle: 'Clinic Cleaning Kochi',
    hero: {
      eyebrow: 'Clinic & healthcare cleaning',
      title: 'Clinic Cleaning Service in Kochi',
      description: `Professional clinic and healthcare facility cleaning in Kochi. Sanitization, hygiene. Kakkanad, Edappally, Kaloor, Vyttila and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Flexible timings' },
        { icon: 'Shield', text: 'Hygiene-focused' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Waiting area & consultation room cleaning',
      'Sanitization of surfaces',
      'Restroom cleaning',
      'Floor & corridor cleaning',
    ],
    areasWeServe: [
      'Kakkanad',
      'Edappally',
      'Kaloor',
      'Vyttila',
      'Palarivattom',
      'Kadavanthra',
      'Tripunithura',
      'Aluva',
      'Kalamassery',
      'Angamaly',
    ],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book clinic cleaning in Kochi',
      description: 'Hygienic clinic cleaning. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Clinic Cleaning Kochi | Healthcare Cleaning Service | Tools & Bubbles',
      metaDescription: `Clinic cleaning in Kochi. Sanitization for clinics and healthcare. Kakkanad, Edappally, Vyttila. Call 9995216660.`,
      keywords: 'clinic cleaning kochi, healthcare cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles offers professional clinic and healthcare facility cleaning in Kochi. We serve Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly and all Kochi with sanitisation and hygiene-focused cleaning.`,
        `Clinics and healthcare spaces need consistent sanitisation of waiting areas, consultation rooms, and restrooms. Our team focuses on high-touch surfaces and uses appropriate disinfectants. We can schedule daily, weekly, or as needed. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What does clinic cleaning include?',
          paragraphs: [
            `We clean waiting areas and consultation rooms, sanitise surfaces, clean restrooms, and clean floors and corridors. We use disinfectants suitable for healthcare environments.`,
          ],
          bulletList: [
            'Waiting area and consultation room cleaning',
            'Surface sanitisation',
            'Restroom cleaning',
            'Floor and corridor cleaning',
          ],
        },
        {
          heading: 'Where do you provide clinic cleaning in Kochi?',
          paragraphs: [
            `We provide clinic cleaning across Kochi and Ernakulam: Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and nearby localities.`,
          ],
        },
      ],
      conclusion: 'Book clinic cleaning in Kochi today. Call 9995216660 or book online.',
    },
    faq: [
      {
        question: 'Do you use disinfectants suitable for clinics?',
        answer:
          'Yes. We use disinfectants and methods appropriate for healthcare environments and high-touch surfaces.',
      },
      {
        question: 'Can you clean daily or weekly?',
        answer:
          'Yes. We can schedule daily, weekly, or custom frequency depending on your clinic size and footfall.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Palarivattom, Kadavanthra, Tripunithura, Aluva, Kalamassery, Angamaly, and all Kochi.',
      },
    ],
    images: SERVICE_IMAGES.clinic,
  },

  'warehouse-cleaning-kochi': {
    slug: 'warehouse-cleaning-kochi',
    serviceName: 'Warehouse Cleaning Service',
    serviceTitle: 'Warehouse Cleaning Kochi',
    hero: {
      eyebrow: 'Warehouse & industrial cleaning',
      title: 'Warehouse Cleaning Service in Kochi',
      description: `Professional warehouse and industrial cleaning in Kochi. Large spaces, periodic deep cleaning. Kakkanad, Edappally, Aluva, Kalamassery and all Kochi.`,
      highlights: [
        { icon: 'Clock', text: 'Scheduled slots' },
        { icon: 'Shield', text: 'Equipment-ready' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Floor sweeping & mopping',
      'Dust & debris removal',
      'Loading bay cleaning',
      'Office area within warehouse',
    ],
    areasWeServe: ['Kakkanad', 'Edappally', 'Kaloor', 'Vyttila', 'Aluva', 'Kalamassery', 'Angamaly'],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book warehouse cleaning in Kochi',
      description: 'Warehouse and industrial cleaning. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Warehouse Cleaning Kochi | Warehouse Cleaning Service | Tools & Bubbles',
      metaDescription: `Warehouse cleaning in Kochi. Industrial and warehouse cleaning. Kakkanad, Edappally, Aluva. Call 9995216660.`,
      keywords: 'warehouse cleaning kochi, industrial cleaning kochi',
      introParagraphs: [
        `Tools & Bubbles provides professional warehouse and industrial cleaning in Kochi. We serve Kakkanad, Edappally, Kaloor, Vyttila, Aluva, Kalamassery, Angamaly and all Kochi. Large spaces need regular sweeping, mopping, and debris removal to stay safe and presentable.`,
        `We clean warehouse floors, loading bays, and office areas within the facility. We can schedule periodic deep cleans or regular upkeep. Equipment and scope are tailored to your warehouse size. Call 9995216660 for a quote.`,
      ],
      sections: [
        {
          heading: 'What does warehouse cleaning include?',
          paragraphs: [
            `We perform floor sweeping and mopping, dust and debris removal, loading bay cleaning, and office area cleaning within the warehouse. We do not handle hazardous materials or specialised industrial waste—only general cleaning.`,
          ],
          bulletList: [
            'Floor sweeping and mopping',
            'Dust and debris removal',
            'Loading bay cleaning',
            'Office area within warehouse',
          ],
        },
        {
          heading: 'Where do you provide warehouse cleaning in Kochi?',
          paragraphs: [
            `We provide warehouse cleaning in Kakkanad, Edappally, Kaloor, Vyttila, Aluva, Kalamassery, Angamaly, and across Kochi and Ernakulam. Call for a custom quote based on square footage.`,
          ],
        },
      ],
      conclusion:
        'Book warehouse cleaning in Kochi today. Call 9995216660 for a quote or to schedule periodic cleaning.',
    },
    faq: [
      {
        question: 'Do you clean large warehouses?',
        answer:
          'Yes. We clean warehouses of various sizes. Scope and pricing depend on square footage and frequency. Call 9995216660 for a quote.',
      },
      {
        question: 'Which areas do you cover?',
        answer:
          'We cover Kakkanad, Edappally, Kaloor, Vyttila, Aluva, Kalamassery, Angamaly, and all Kochi and Ernakulam.',
      },
    ],
    images: SERVICE_IMAGES.warehouse,
  },

  // Infopark SEO pages
  'office-cleaning-infopark': {
    slug: 'office-cleaning-infopark',
    serviceName: 'Office Cleaning Infopark',
    serviceTitle: 'Office Cleaning Infopark Kakkanad',
    hero: {
      eyebrow: 'Infopark Kakkanad',
      title: 'Office Cleaning Infopark Kakkanad',
      description: `Professional office cleaning for IT companies in Infopark, Kakkanad. Daily, weekly and deep cleaning. Same-day slots and contract cleaning.`,
      highlights: [
        { icon: 'Clock', text: 'Before/after office hours' },
        { icon: 'Shield', text: 'NDA-friendly' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Workstation & desk cleaning',
      'Pantry & restroom cleaning',
      'Reception & lobby',
      'Floor & vacuum cleaning',
    ],
    areasWeServe: ['Infopark Phase 1', 'Infopark Phase 2', 'SmartCity', 'Kakkanad', 'Thrikkakara'],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book office cleaning in Infopark',
      description: 'Corporate cleaning for Infopark offices. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Office Cleaning Infopark Kakkanad | IT Park Cleaning | Tools & Bubbles',
      metaDescription: `Office cleaning in Infopark Kakkanad. Daily and deep cleaning for IT companies. Call 9995216660.`,
      keywords: 'office cleaning infopark, office cleaning infopark kakkanad, infopark cleaning',
      introParagraphs: [
        `Tools & Bubbles provides professional office cleaning in Infopark, Kakkanad. We serve IT companies in Infopark Phase 1, Phase 2, SmartCity and Thrikkakara with daily, weekly and deep cleaning. Many tech companies and startups in Infopark use us for contract cleaning so their offices stay spotless without disrupting work.`,
        `We clean workstations, pantries, restrooms, reception, and floors. Our teams work before or after office hours and can sign NDAs where required. We are used to building access and security procedures in IT parks. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What is included in office cleaning in Infopark?',
          paragraphs: [
            `We offer workstation and desk cleaning, pantry and restroom cleaning, reception and lobby cleaning, and floor and vacuum cleaning. We can do daily, weekly, or one-time deep cleaning for offices in Infopark Phase 1, Phase 2, and SmartCity.`,
          ],
          bulletList: [
            'Workstation and desk cleaning',
            'Pantry and restroom cleaning',
            'Reception and lobby',
            'Floor and vacuum cleaning',
            'Before/after hours to suit IT schedules',
          ],
        },
        {
          heading: 'Why choose Tools & Bubbles for Infopark office cleaning?',
          paragraphs: [
            `We are familiar with Infopark buildings, security checks, and access norms. Our staff are background-verified and we respect confidentiality. We offer transparent pricing and flexible contracts. Call 9995216660 for a quote.`,
          ],
        },
      ],
      conclusion:
        'Book office cleaning in Infopark today. Call 9995216660 or book online for daily, weekly, or deep cleaning.',
    },
    faq: [
      {
        question: 'Do you clean offices in Infopark Phase 1 and Phase 2?',
        answer:
          'Yes. We clean offices in Infopark Phase 1, Phase 2, SmartCity, and Thrikkakara. We work around your office hours.',
      },
      {
        question: 'Can you sign an NDA for Infopark offices?',
        answer: 'Yes. We respect confidentiality and can sign NDAs where required by IT companies.',
      },
      {
        question: 'Do you offer contract cleaning for Infopark?',
        answer:
          'Yes. Many Infopark companies use us for daily or weekly contract cleaning. Call 9995216660 to discuss.',
      },
    ],
    images: SERVICE_IMAGES.infopark,
  },

  'commercial-cleaning-infopark': {
    slug: 'commercial-cleaning-infopark',
    serviceName: 'Commercial Cleaning Infopark',
    serviceTitle: 'Commercial Cleaning Infopark',
    hero: {
      eyebrow: 'Infopark Kakkanad',
      title: 'Commercial Cleaning Infopark',
      description: `Professional commercial cleaning for Infopark, Kakkanad. Offices, common areas, cafeterias. Contract and one-time cleaning.`,
      highlights: [
        { icon: 'Clock', text: 'Flexible timings' },
        { icon: 'Shield', text: 'Corporate contracts' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: [
      'Office & common area cleaning',
      'Pantry & restroom sanitization',
      'Reception & lobby',
      'Floor & window cleaning',
    ],
    areasWeServe: ['Infopark Phase 1', 'Infopark Phase 2', 'SmartCity', 'Kakkanad', 'Thrikkakara'],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book commercial cleaning in Infopark',
      description: 'Corporate cleaning contracts. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Commercial Cleaning Infopark | Infopark Kakkanad Cleaning | Tools & Bubbles',
      metaDescription: `Commercial cleaning in Infopark Kakkanad. Offices, common areas. Contract cleaning. Call 9995216660.`,
      keywords: 'commercial cleaning infopark, infopark cleaning service, infopark kakkanad cleaning',
      introParagraphs: [
        `Tools & Bubbles offers commercial cleaning in Infopark, Kakkanad. We serve IT parks, offices, and common areas in Infopark Phase 1, Phase 2, SmartCity, and Thrikkakara. We provide both contract cleaning and one-time deep cleans for offices, lobbies, cafeterias, and common spaces.`,
        `Our teams work before or after business hours so your operations are not disrupted. We clean workstations, pantries, restrooms, reception areas, and floors. Many Infopark buildings and facility managers use us for building-wide or tenant-specific cleaning. Call 9995216660 for a quote.`,
      ],
      sections: [
        {
          heading: 'What does commercial cleaning in Infopark include?',
          paragraphs: [
            `We offer office and common area cleaning, pantry and restroom sanitisation, reception and lobby cleaning, and floor and window cleaning. We can tailor the scope for a single office or an entire building and offer daily, weekly, or one-time service.`,
          ],
          bulletList: [
            'Office and common area cleaning',
            'Pantry and restroom sanitisation',
            'Reception and lobby',
            'Floor and window cleaning',
            'Contract or one-time options',
          ],
        },
        {
          heading: 'Where do you provide commercial cleaning in Infopark?',
          paragraphs: [
            `We provide commercial cleaning in Infopark Phase 1, Phase 2, SmartCity, Kakkanad, and Thrikkakara. We are used to working with building security and access procedures.`,
          ],
        },
      ],
      conclusion:
        'Book commercial cleaning in Infopark today. Call 9995216660 or book online for contracts or one-time deep cleans.',
    },
    faq: [
      {
        question: 'Do you clean common areas in Infopark buildings?',
        answer:
          'Yes. We clean lobbies, common corridors, pantries, and restrooms. We can work with facility managers for building-wide contracts.',
      },
      {
        question: 'Can you clean after office hours?',
        answer: 'Yes. We schedule before or after business hours so we do not disturb employees.',
      },
      {
        question: 'Which Infopark areas do you cover?',
        answer: 'We cover Infopark Phase 1, Phase 2, SmartCity, Kakkanad, and Thrikkakara.',
      },
    ],
    images: SERVICE_IMAGES.infopark,
  },

  'cleaning-service-infopark': {
    slug: 'cleaning-service-infopark',
    serviceName: 'Cleaning Service Infopark',
    serviceTitle: 'Cleaning Service Infopark',
    hero: {
      eyebrow: 'Infopark Kakkanad',
      title: 'Cleaning Service Infopark',
      description: `Professional cleaning service for Infopark, Kakkanad. Deep cleaning, office cleaning, home cleaning for residents and offices in Infopark.`,
      highlights: [
        { icon: 'Clock', text: 'Same-day slots' },
        { icon: 'Shield', text: 'Verified staff' },
        { icon: 'Star', text: '4.9★ rated' },
      ],
    },
    servicesIncluded: ['Office cleaning', 'Deep cleaning', 'Home cleaning', 'Pantry & restroom cleaning'],
    areasWeServe: ['Infopark Phase 1', 'Infopark Phase 2', 'SmartCity', 'Kakkanad', 'Thrikkakara'],
    whyChoose: defaultWhyChoose,
    reviews: defaultReviews,
    cta: {
      title: 'Book cleaning service in Infopark',
      description: 'Offices and homes in Infopark. Book online or call 9995216660.',
      phoneText: 'Call 9995216660',
    },
    seo: {
      metaTitle: 'Cleaning Service Infopark | Infopark Kakkanad Cleaning | Tools & Bubbles',
      metaDescription: `Cleaning service in Infopark Kakkanad. Office and home cleaning. Same-day booking. Call 9995216660.`,
      keywords: 'cleaning service infopark, cleaning infopark kakkanad, infopark cleaning',
      introParagraphs: [
        `Tools & Bubbles provides cleaning services in Infopark, Kakkanad. We offer office cleaning, deep cleaning, and home cleaning for Infopark Phase 1, Phase 2, SmartCity, and Thrikkakara. Whether you run an office in the IT park or live in an apartment nearby, we cover both commercial and residential cleaning.`,
        `We serve tech companies, startups, and residents in and around Infopark. Our teams are used to building access and security norms. We offer same-day and next-day slots, transparent pricing, and contract options for offices. Book online or call 9995216660.`,
      ],
      sections: [
        {
          heading: 'What cleaning services do you offer in Infopark?',
          paragraphs: [
            `We offer office cleaning (daily, weekly, or deep clean), home and apartment deep cleaning, and pantry and restroom cleaning. We can also combine services—for example, office contract cleaning plus occasional deep cleaning.`,
          ],
          bulletList: [
            'Office cleaning (daily, weekly, deep)',
            'Home and apartment deep cleaning',
            'Pantry and restroom cleaning',
            'Same-day and next-day slots',
            'Contract and one-time options',
          ],
        },
        {
          heading: 'Where exactly do you serve in Infopark?',
          paragraphs: [
            `We serve Infopark Phase 1, Phase 2, SmartCity, Kakkanad, and Thrikkakara. We clean offices inside the IT park and homes in the surrounding residential areas.`,
          ],
        },
      ],
      conclusion:
        'Book cleaning service in Infopark today. Call 9995216660 or book online for office or home cleaning.',
    },
    faq: [
      {
        question: "How long does deep cleaning take for an IT professional's apartment in Kakkanad Infopark?",
        answer:
          'A typical 2BHK near Infopark Phase 1 or 2 takes 4–6 hours. Book a weekday afternoon slot online so you are not home for the full job. 3BHKs and villas on Seaport-Airport Road usually need 6–8 hours.',
      },
      {
        question: 'Do you clean homes near Infopark?',
        answer:
          'Yes. We clean apartments and homes in Kakkanad, Thrikkakara, and other areas near Infopark for residents who work in the IT park.',
      },
      {
        question: 'Do you offer same-day cleaning in Infopark?',
        answer:
          'Yes. Same-day and next-day slots are often available for both office and home cleaning. Book online or call 9995216660.',
      },
      {
        question: 'Which Infopark areas do you cover?',
        answer:
          'We cover Infopark Phase 1, Phase 2, SmartCity, Kakkanad, and Thrikkakara for office and home cleaning.',
      },
    ],
    images: SERVICE_IMAGES.infopark,
  },
  ...KEYWORD_SERVICE_PAGE_CONFIGS,
};

export function getServiceConfig(pathSlug: string): ServicePageConfig | null {
  const slug = pathSlug.replace(/^\//, '');
  return SERVICE_PAGE_CONFIGS[slug] ?? null;
}

/**
 * SEO landing path for a `service_pricing.slug` (home catalog tiers → dedicated pages).
 * Residential/commercial tiers map via `bookingTierSlug` on tier landing configs; all post-construction tiers share one page.
 */
export function getLandingPathForTierSlug(tierSlug: string | undefined | null): string | undefined {
  if (!tierSlug?.trim()) return undefined;
  const s = tierSlug.trim();
  for (const cfg of Object.values(SERVICE_PAGE_CONFIGS)) {
    if (cfg.bookingTierSlug === s) {
      return `/${cfg.slug}`;
    }
  }
  if (s.startsWith('post-construction-cleaning-')) {
    return `/post-construction-cleaning-kochi?tier=${encodeURIComponent(s)}`;
  }
  return undefined;
}

/** All pathnames that should render the SEO service page component (for App routing). */
export function getServicePagePaths(): string[] {
  return Object.keys(SERVICE_PAGE_CONFIGS).map((slug) => `/${slug}`);
}

/** Navigation links for all SEO service pages (for Header / sitemap). */
export const SERVICE_NAV_LINKS: { label: string; href: string }[] = Object.entries(SERVICE_PAGE_CONFIGS).map(
  ([slug, config]) => ({
    label: config.serviceTitle,
    href: `/${slug}`,
  }),
);
